// client/src/pages/admin/InboxEmailParts.tsx
// Attachment chips + rolled-up thread viewer for the Admin Team inbox cards. These mirror
// the Dev Team board's AttachmentChips / ThreadViewer (DevTeam.tsx) — same look, same
// stored-data source — pointed at /api/desks-inbox instead of /api/dev-team.

import React, { useEffect, useState } from 'react';
import { Mailbox, Paperclip } from 'lucide-react';
import { fetchInboxThread, inboxAttachmentUrl, AttachmentRef, InboxItem, InboxThreadMessage } from '../../services/adminInbox';

function fmtBytes(n: number | null): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

// Download chips for an inbox email's file attachments. Each links to the
// mode=attachment proxy, which streams the file from func's blob storage. All
// attachments (root or thread) resolve under the same bugReportId.
export function AttachmentChips({ bugReportId, attachments }: { bugReportId: number; attachments?: AttachmentRef[] }) {
  if (!attachments || !attachments.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
      {attachments.map((a) => (
        <a key={a.id} href={inboxAttachmentUrl(bugReportId, a.id)} target="_blank" rel="noreferrer"
          title={`Download ${a.name}${a.size ? ` (${fmtBytes(a.size)})` : ''}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 220, textDecoration: 'none', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 8, padding: '3px 9px', fontSize: 11 }}>
          <Paperclip size={12} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
        </a>
      ))}
    </div>
  );
}

const fmt = (iso: string | null) => {
  if (!iso) return '';
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleString() : '';
};

// Read-only viewer for an inbox email: the ORIGINAL message (the card itself — the first
// mail to the team) followed by every reply captured into ContextJson.thread[]. Replies
// lazy-load on open so the board list stays lean.
export function InboxThreadViewer({ item, nameOf, onClose }: {
  item: InboxItem; nameOf: (email: string) => string; onClose: () => void;
}) {
  const [msgs, setMsgs] = useState<InboxThreadMessage[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMsgs(null); setErr(null);
    fetchInboxThread(item.bugReportId)
      .then((t) => { if (!cancelled) setMsgs(t); })
      .catch((e) => { if (!cancelled) setErr(e?.message || 'Could not load the thread.'); });
    return () => { cancelled = true; };
  }, [item.bugReportId]);

  // Appended in arrival order already, but sort defensively so the thread always reads oldest→newest.
  const sorted = msgs ? [...msgs].sort((a, b) => (Date.parse(a.receivedAt || '') || 0) - (Date.parse(b.receivedAt || '') || 0)) : null;
  const total = 1 + (msgs?.length ?? 0);

  const Bubble = ({ who, when, subject, body, tag, attachments }: { who: string; when: string; subject?: string | null; body?: string | null; tag?: string; attachments?: AttachmentRef[] }) => (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--dm-inset)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <strong style={{ fontSize: 12.5 }}>{who}</strong>
        {tag && <span className="badge" style={{ fontSize: 9, background: 'var(--dm-chip)', color: 'var(--muted)' }}>{tag}</span>}
        {when && <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>{when}</span>}
      </div>
      {subject && <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>{subject}</div>}
      <div style={{ fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{body || <span className="muted">(no message text)</span>}</div>
      <AttachmentChips bugReportId={item.bugReportId} attachments={attachments} />
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--dm-scrim)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(640px, 100%)', maxHeight: '90vh', overflow: 'auto', padding: 0, background: 'var(--dm-surface-grad)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, background: 'var(--dm-head)', zIndex: 1 }}>
          <Mailbox size={16} style={{ color: 'var(--accent)' }} />
          <strong style={{ fontSize: 14 }}>Email thread</strong>
          <span className="badge" style={{ fontSize: 10, background: 'var(--dm-chip)', color: 'var(--muted)' }}>{total} message{total === 1 ? '' : 's'}</span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* The original message — this is the card itself (the first mail to the team). */}
          <Bubble who={nameOf(item.from)} when={fmt(item.receivedAt)} subject={item.subject} body={item.preview || null} tag="Original" attachments={item.attachments} />
          {err && <div style={{ color: '#f87171', fontSize: 12 }}>{err}</div>}
          {!sorted && !err && <div className="muted" style={{ fontSize: 12, textAlign: 'center', padding: 8 }}>Loading replies…</div>}
          {sorted && sorted.map((m, i) => (
            <Bubble key={m.messageId || i} who={m.from ? nameOf(m.from) : '(unknown sender)'} when={fmt(m.receivedAt)} subject={m.subject} body={m.body} attachments={m.attachments} />
          ))}
        </div>
      </div>
    </div>
  );
}
